import { acpTokens } from './tokens'

const { acp: c } = acpTokens.colors
const v = (t: { value: string }) => t.value

export const acpGlobalStyles = {
  html: {
    colorPalette: 'blue',
    overflowX: 'hidden',
    // Root scale aligned with the blog (blog/src/styles/global.css → 17px) so a
    // given rem/fontSize renders at the same size across both surfaces.
    fontSize: '17px',
    scrollBehavior: 'smooth',
  },

  body: {
    bg: v(c.bg),
    color: v(c.ink),
    fontFamily: acpTokens.fonts.body.value,
    lineHeight: 1.65,
    // Jersey is a single-weight (400) pixel font with no bold face — disable
    // synthetic bold so headings render its true glyphs (Hanken/body keep their
    // real 700 face). Removes the "too heavy" faux-bold on Jersey headings.
    fontSynthesisWeight: 'none',
    WebkitFontSmoothing: 'antialiased',
    overflowX: 'hidden',
    touchAction: 'pan-x pan-y',
  },

  '::selection': {
    backgroundColor: v(c.blue),
    color: 'white',
  },

  // ACP Card utilities
  '.acp-card': {
    borderRadius: acpTokens.radii.card.value + ' !important',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: 'white !important',
    borderWidth: '1px !important',
    borderColor: `${v(c.line)} !important`,
    borderStyle: 'solid !important',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06) !important',
    position: 'relative' as const,
  },
  '.acp-card-default': {},
  '.acp-card-elevated': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04) !important',
  },
  '.acp-card-outlined': {
    boxShadow: 'none !important',
    borderColor: `${v(c.lineStrong)} !important`,
  },
  '.acp-card-subtle': {
    backgroundColor: `${v(c.surfaceAlt)} !important`,
    boxShadow: 'none !important',
  },
  '.acp-card-hoverable:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1) !important',
    transform: 'translateY(-2px)',
  },
  '.acp-card-clickable': {
    cursor: 'pointer',
  },
  '.acp-card-clickable:active': {
    transform: 'translateY(0)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06) !important',
  },

  // ── ACP brand marketing utilities (Learn home / shell) ──────────
  // Pixel "HUD voice" eyebrow: Jersey 25, blue, uppercase, with a short dash.
  '.acp-eyebrow': {
    fontFamily: acpTokens.fonts.eyebrow.value,
    fontWeight: 400,
    fontSize: '1.02rem',
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
    color: v(c.blue),
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    lineHeight: 1,
  },
  '.acp-eyebrow::before': {
    content: '""',
    width: '1.6rem',
    height: '1px',
    background: v(c.lineStrong),
  },
  // Centered variant: same HUD voice, but a dash on BOTH sides (use when the
  // eyebrow is horizontally centered instead of left-aligned).
  '.acp-eyebrow-centered': {
    fontFamily: acpTokens.fonts.eyebrow.value,
    fontWeight: 400,
    fontSize: '1.02rem',
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
    color: v(c.blue),
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    lineHeight: 1,
  },
  '.acp-eyebrow-centered::before, .acp-eyebrow-centered::after': {
    content: '""',
    width: '1.6rem',
    height: '1px',
    background: v(c.lineStrong),
  },
  // Big pixel display heading: Jersey 15.
  '.acp-display': {
    fontFamily: acpTokens.fonts.display.value,
    fontWeight: 400,
    lineHeight: 1.05,
    letterSpacing: '0.01em',
    color: v(c.ink),
  },

  // Rich course card (cover + body + contextual CTA line).
  '.acp-course-cover': {
    aspectRatio: '16 / 9',
    backgroundColor: v(c.surfaceAlt),
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    borderBottom: `1px solid ${v(c.line)}`,
    position: 'relative' as const,
  },
  '.acp-cta-line': {
    fontWeight: 600,
    color: v(c.blue),
    fontSize: '0.95rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  // Transition lives on the resting state so the arrow eases both in AND out.
  '.acp-cta-line svg': {
    transition: 'transform 0.16s',
  },
  '.acp-course-card:hover .acp-cta-line svg': {
    transform: 'translateX(3px)',
  },

  // Progress bar (blue → green gradient fill).
  '.acp-bar': {
    height: '7px',
    borderRadius: '999px',
    background: v(c.surfaceAlt),
    overflow: 'hidden',
    border: `1px solid ${v(c.line)}`,
  },
  '.acp-bar > span': {
    display: 'block',
    height: '100%',
    borderRadius: '999px',
    background: `linear-gradient(90deg, ${v(c.blue)}, ${v(c.green)})`,
  },

  // Stat icon chip (gold / green / purple / blue washes).
  '.acp-stat-ico': {
    width: '42px',
    height: '42px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    flex: 'none',
  },

  // ACP Input utilities
  '.acp-input': {
    borderRadius: acpTokens.radii.button.value,
    borderWidth: '1px',
    borderColor: v(c.line),
    backgroundColor: 'white',
    color: v(c.ink),
    fontFamily: acpTokens.fonts.body.value,
    fontSize: '0.875rem',
    padding: '0.5rem 0.75rem',
    transition: 'all 0.15s ease',
    width: '100%',
    '&:focus': {
      borderColor: v(c.blue),
      boxShadow: `0 0 0 3px rgba(5, 112, 179, 0.15)`,
      outline: 'none',
    },
    '&:hover:not(:focus)': {
      borderColor: v(c.lineStrong),
    },
    '&::placeholder': {
      color: v(c.inkFaint),
    },
  },
  '.acp-input-error': {
    borderColor: '#e53e3e',
    '&:focus': {
      borderColor: '#e53e3e',
      boxShadow: '0 0 0 3px rgba(229, 62, 62, 0.15)',
    },
  },

  // ACP Spinner utilities
  '.acp-spinner-primary': { color: v(c.blue) },
  '.acp-spinner-accent': { color: v(c.blueLight) },
  '.acp-spinner-gold': { color: v(c.gold) },
  '.acp-spinner-minimal': { color: v(c.inkFaint) },

  // Animations
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.5, transform: 'scale(0.85)' },
  },
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.4 },
  },
  '@keyframes slideIn': {
    from: { transform: 'translateX(100%)', opacity: 0 },
    to: { transform: 'translateX(0)', opacity: 1 },
  },
  '@keyframes confettiFloat': {
    '0%': { transform: 'translateY(0) rotate(0deg)', opacity: 1 },
    '100%': { transform: 'translateY(-120px) rotate(720deg)', opacity: 0 },
  },
  '@keyframes celebrationScale': {
    '0%': { transform: 'scale(0)' },
    '50%': { transform: 'scale(1.2)' },
    '100%': { transform: 'scale(1)' },
  },

  // ── Modal / toast animations (replaces framer-motion) ──────────
  '@keyframes modalBackdropIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  '@keyframes modalBackdropOut': {
    from: { opacity: 1 },
    to: { opacity: 0 },
  },
  '@keyframes modalContentIn': {
    from: { opacity: 0, transform: 'translateY(20px) scale(0.95)' },
    to: { opacity: 1, transform: 'translateY(0) scale(1)' },
  },
  '@keyframes modalContentOut': {
    from: { opacity: 1, transform: 'translateY(0) scale(1)' },
    to: { opacity: 0, transform: 'translateY(-10px) scale(0.95)' },
  },
  '.acp-modal-backdrop-enter': {
    animation: 'modalBackdropIn 0.2s ease-out both',
  },
  '.acp-modal-backdrop-exit': {
    animation: 'modalBackdropOut 0.2s ease-in both',
  },
  '.acp-modal-content-enter': {
    animation: 'modalContentIn 0.2s ease-out 0.05s both',
  },
  '.acp-modal-content-exit': {
    animation: 'modalContentOut 0.2s ease-in both',
  },
  '.acp-toast-enter': {
    animation: 'modalContentIn 0.3s ease-out both',
  },
  '.acp-toast-exit': {
    animation: 'modalContentOut 0.3s ease-in both',
  },

  '.acp-shimmer': {
    display: 'inline-block',
    background: `linear-gradient(90deg, ${v(c.bgSoft)} 25%, ${v(c.bg)} 50%, ${v(c.bgSoft)} 75%)`,
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: '4px',
  },
}
